import React, { useState, useEffect } from 'react';
import { Modal, Button, Select, message, Popconfirm, Spin, Row, Col, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { MenuOutlined } from '@ant-design/icons';
import { EligibleApprover } from '../../../types/user.types';
import { ApprovalHierarchyStepPayload, ApprovalModule } from '../../../types/approval.types';
import userService from '../../../services/user/user.service';
import approvalHierarchyService from '../../../services/approvalHierarchy/approvalHierarchy.service';
import { handleApiError } from '../../../utils/errorHandler';

const { Option } = Select;
const { Text } = Typography;

const MAX_STEPS = 5;

const MODULE_LABELS: Record<ApprovalModule, string> = {
  field_mode: 'Saha Modu Onay Hiyerarşisi',
  field_activity: 'Aktivite Onay Hiyerarşisi',
};

interface ApprovalHierarchyModalProps {
  visible: boolean;
  onClose: () => void;
  onHierarchyUpdate?: () => void;
  module: ApprovalModule;
  department: { id: number; name: string } | null;
}

const ApprovalHierarchyModal: React.FC<ApprovalHierarchyModalProps> = ({
  visible,
  onClose,
  onHierarchyUpdate,
  module,
  department
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [eligibleApprovers, setEligibleApprovers] = useState<EligibleApprover[]>([]);
  const [steps, setSteps] = useState<ApprovalHierarchyStepPayload[]>([]);
  const [hasExistingHierarchy, setHasExistingHierarchy] = useState(false);

  useEffect(() => {
    if (visible && department) {
      fetchData();
    } else {
      setSteps([]);
      setHasExistingHierarchy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, department?.id, module]);

  const fetchData = async () => {
    if (!department) return;
    setLoading(true);
    try {
      const [approvers, hierarchy] = await Promise.all([
        userService.getEligibleApprovers(),
        approvalHierarchyService.getHierarchy(module, department.id)
      ]);
      setEligibleApprovers(approvers);
      const existingSteps = [...(hierarchy?.steps || [])].sort((a, b) => a.stepOrder - b.stepOrder);
      setSteps(existingSteps.map((step, index) => ({ approverId: step.approver.id, stepOrder: index + 1 })));
      setHasExistingHierarchy(existingSteps.length > 0);
    } catch (error) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStep = () => {
    if (steps.length >= MAX_STEPS) {
      message.warning(`En fazla ${MAX_STEPS} onaycı ekleyebilirsiniz.`);
      return;
    }
    setSteps([...steps, { approverId: 0, stepOrder: steps.length + 1 }]);
  };

  const handleRemoveStep = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index).map((step, i) => ({ ...step, stepOrder: i + 1 })));
  };

  const handleApproverChange = (approverId: number, index: number) => {
    setSteps(steps.map((step, i) => (i === index ? { ...step, approverId } : step)));
  };

  const handleSave = async () => {
    if (!department) return;

    if (steps.some(step => !step.approverId)) {
      message.error('Lütfen her adım için bir onaycı seçin.');
      return;
    }

    const approverIds = steps.map(step => step.approverId);
    if (new Set(approverIds).size !== approverIds.length) {
      message.error('Aynı onaycıyı birden fazla ekleyemezsiniz.');
      return;
    }

    setLoading(true);
    try {
      await approvalHierarchyService.saveHierarchy(module, department.id, steps);
      message.success(steps.length > 0 ? 'Hiyerarşi başarıyla kaydedildi.' : 'Hiyerarşi kaldırıldı.');
      onHierarchyUpdate?.();
      onClose();
    } catch (error) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!department) return;
    setLoading(true);
    try {
      await approvalHierarchyService.deleteHierarchy(module, department.id);
      message.success('Hiyerarşi başarıyla silindi.');
      onHierarchyUpdate?.();
      onClose();
    } catch (error) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;
    const items = Array.from(steps);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setSteps(items.map((item, index) => ({ ...item, stepOrder: index + 1 })));
  };

  const approverInUse = (approverId: number) => steps.some(step => step.approverId === approverId);

  return (
    <Modal
      title={`${MODULE_LABELS[module]}${department ? ` - ${department.name}` : ''}`}
      open={visible}
      onCancel={onClose}
      footer={[
        hasExistingHierarchy && (
          <Popconfirm
            key="delete"
            title="Bu hiyerarşiyi silmek istediğinizden emin misiniz?"
            onConfirm={handleDelete}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button danger loading={loading}>
              Sil
            </Button>
          </Popconfirm>
        ),
        <Button key="back" onClick={onClose}>
          İptal
        </Button>,
        <Button key="submit" loading={loading} onClick={handleSave} className="bg-dinox-purple text-white">
          Kaydet
        </Button>,
      ]}
      width={700}
    >
      <Spin spinning={loading}>
        <Text type="secondary" className="d-block mb-3">
          Talepler sırayla bu onaycılara gider. Hiyerarşi tanımlanmazsa talep, şirket yöneticisi veya çalışanın
          departman yöneticisi tarafından tek adımda onaylanır.
        </Text>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={`approval-steps-${module}`}>
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {steps.map((step, index) => (
                  <Draggable key={index} draggableId={`approval-step-${module}-${index}`} index={index}>
                    {(dragProvided) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        style={{
                          userSelect: 'none',
                          padding: '10px',
                          margin: '0 0 8px 0',
                          backgroundColor: '#f9f9f9',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          ...dragProvided.draggableProps.style,
                        }}
                      >
                        <Row align="middle" gutter={16}>
                          <Col {...dragProvided.dragHandleProps} style={{ cursor: 'grab' }}>
                            <MenuOutlined />
                          </Col>
                          <Col>
                            <span style={{ fontWeight: 'bold' }}>{index + 1}. Onaycı:</span>
                          </Col>
                          <Col flex="auto">
                            <Select
                              showSearch
                              style={{ width: '100%' }}
                              placeholder="Onaycı Seçin"
                              value={step.approverId || null}
                              onChange={(value) => handleApproverChange(value, index)}
                              filterOption={(input, option) =>
                                String(option?.children).toLowerCase().includes(input.toLowerCase())
                              }
                            >
                              {eligibleApprovers.map(approver => (
                                <Option
                                  key={approver.id}
                                  value={approver.id}
                                  disabled={approverInUse(approver.id) && step.approverId !== approver.id}
                                >
                                  {`${approver.firstName} ${approver.lastName} (${approver.role})`}
                                </Option>
                              ))}
                            </Select>
                          </Col>
                          <Col>
                            <Button type="primary" danger onClick={() => handleRemoveStep(index)}>
                              Sil
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <Button
          type="dashed"
          onClick={handleAddStep}
          disabled={steps.length >= MAX_STEPS}
          style={{ width: '100%', marginTop: '10px' }}
        >
          + Onaycı Ekle
        </Button>
      </Spin>
    </Modal>
  );
};

export default ApprovalHierarchyModal;
